import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ScreenTitle } from "@/components/ScreenTitle";
import { RefreshCw } from "lucide-react";

const LEVELS = ["", "info", "success", "warn", "error"];
const STAGES = ["", "fetch", "llm", "wordpress", "scheduler", "test"];

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [level, setLevel] = useState("");
  const [stage, setStage] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = { limit: 300 };
    if (level) params.level = level;
    if (stage) params.stage = stage;
    try {
      const r = await api.get("/logs", { params });
      setLogs(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, stage]);

  return (
    <div data-testid="logs-page">
      <ScreenTitle
        eyebrow="Traces d'exécution"
        title="Journal"
        right={
          <button
            onClick={load}
            data-testid="logs-refresh"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:text-black text-[var(--text-secondary)]"
            aria-label="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.75} />
          </button>
        }
      />

      <div className="px-5 pb-3 flex gap-2 overflow-x-auto no-scrollbar">
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          data-testid="logs-level-filter"
          className="min-h-[40px] border border-[var(--border-default)] px-3 font-mono text-[11px] bg-white rounded-none focus:outline-none focus:border-black"
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>{l ? l : "tous niveaux"}</option>
          ))}
        </select>
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          data-testid="logs-stage-filter"
          className="min-h-[40px] border border-[var(--border-default)] px-3 font-mono text-[11px] bg-white rounded-none focus:outline-none focus:border-black"
        >
          {STAGES.map((s) => (
            <option key={s} value={s}>{s ? s : "toutes étapes"}</option>
          ))}
        </select>
      </div>

      <div className="font-mono text-[12px]" data-testid="logs-list">
        {logs.length === 0 && (
          <div className="px-5 py-16 text-center text-[var(--text-muted)]">
            Aucun événement pour ces filtres.
          </div>
        )}
        {logs.map((l) => (
          <div key={l.id} className="border-b border-[var(--border-default)]">
            <button
              onClick={() => setExpanded(expanded === l.id ? null : l.id)}
              className="w-full text-left px-5 py-2.5 flex items-start gap-2 hover:bg-white"
              data-testid={`log-row-${l.id}`}
            >
              <span className="text-[10px] text-[var(--text-muted)] whitespace-nowrap pt-0.5 w-16">
                {new Date(l.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </span>
              <LevelTag level={l.level} />
              <span className="text-[10px] uppercase text-[var(--text-muted)] whitespace-nowrap pt-0.5 w-14">
                {l.stage}
              </span>
              <span className="min-w-0 flex-1 break-words leading-[1.35]">{l.message}</span>
            </button>
            {expanded === l.id && (l.details || l.item_url) && (
              <div className="px-5 pb-3 pt-1 text-[11px] text-[var(--text-secondary)] whitespace-pre-wrap break-all bg-white border-t border-dashed border-[var(--border-default)]">
                {l.feed_name && <div className="mb-1"><span className="caption">Flux :</span> {l.feed_name}</div>}
                {l.item_url && <div className="mb-2"><span className="caption">Item :</span> {l.item_url}</div>}
                {l.details && <div>{l.details}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LevelTag({ level }) {
  const c = {
    error: "text-[var(--accent-strong)]",
    success: "text-[var(--status-success)]",
    warn: "text-[var(--accent)]",
    info: "text-[var(--text-muted)]",
  }[level] || "text-[var(--text-muted)]";
  const l = { error: "ERR", success: "OK ", warn: "WRN", info: "INF" }[level] || "…  ";
  return (
    <span className={`font-bold ${c} pt-0.5 whitespace-nowrap`}>
      {l}
    </span>
  );
}
